import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiArrowLeft } from 'react-icons/fi';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import axiosInstance from '../../services/AxiosInstance';
import { createForum } from '../../services/ForumService';
import { canUploadForumFiles, clearForumSubscriptionCache } from '../../utils/forumAccess';
import { useForumAccess } from '../../hooks/useForumAccess';
import ForumForm from './ForumForm';
import { pickQuerySuffix, withListQuery } from '../../utils/listQueryPersistence';
import '../tasks/addtask.css';

const FORUM_LIST_QUERY_KEYS = [
  'search',
  'status',
  'show_closed',
  'created_start',
  'created_end',
  'participant_id',
  'page',
];

const normalizeApiList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.data)) return payload.data;
  if (payload && Array.isArray(payload.branches)) return payload.branches;
  if (payload && Array.isArray(payload.departments)) return payload.departments;
  if (payload && Array.isArray(payload.subDepartments)) return payload.subDepartments;
  return [];
};

const ForumCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const forumListFallback = withListQuery(
    '/forum/list',
    pickQuerySuffix(location.search, FORUM_LIST_QUERY_KEYS)
  );
  const { hasAccess, loading: accessLoading } = useForumAccess();
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    if (!userData?.corporateId) return;

    const userParams = new URLSearchParams();
    userParams.append('corporateId', parseInt(userData.corporateId, 10));
    if (userData.branchId) {
      userParams.append('branch', userData.branchId);
    }
    axiosInstance.get(`/list/corporateuserslist?${userParams.toString()}`)
      .then((res) => setUsers((res.data.users || []).filter((u) => u.id !== userData.id)))
      .catch(() => {});

    axiosInstance.get(`/settings/branches?corporate_id=${userData.corporateId}`)
      .then((res) => setBranches(normalizeApiList(res.data)))
      .catch(() => {});

    axiosInstance.get(`/settings/departments?corporate_id=${userData.corporateId}`)
      .then((res) => setDepartments(normalizeApiList(res.data)))
      .catch(() => {});

    axiosInstance
      .get(`/settings/subDepartments?corporate_id=${userData.corporateId}`)
      .catch(() => axiosInstance.get(`/settings/subdepartments?corporate_id=${userData.corporateId}`))
      .then((res) => setSubDepartments(normalizeApiList(res.data)))
      .catch(() => {});
  }, []);

  const handleSubmit = async (form) => {
    if (!form.title?.trim()) {
      toast.error('Title is required');
      return;
    }

    const requiredBytes = (form.files || []).reduce((sum, f) => sum + (f.size || 0), 0);
    if (requiredBytes > 0) {
      const uploadCheck = await canUploadForumFiles(requiredBytes);
      if (!uploadCheck.canUpload) {
        toast.error(uploadCheck.message || 'Insufficient forum storage');
        return;
      }
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('title', form.title.trim());
      fd.append('description', form.description || '');
      fd.append('priority', form.priority || 'Medium');
      fd.append('start_date', form.start_date);
      fd.append('email_notification', form.email_notification);
      fd.append('is_favorite', form.is_favorite);
      fd.append('allow_participants_edit', form.allow_participants_edit);
      fd.append('allow_participants_close', form.allow_participants_close);
      fd.append('allow_participants_delete', form.allow_participants_delete);
      fd.append('individuals', JSON.stringify(form.individual_ids.map((id) => ({ id }))));
      fd.append('branches', JSON.stringify(form.branch_ids.map((id) => ({ id }))));
      fd.append('departments', JSON.stringify(form.department_ids.map((id) => ({ id }))));
      fd.append('sub_departments', JSON.stringify(form.sub_department_ids.map((id) => ({ id }))));
      (form.files || []).forEach((f) => fd.append('files', f));

      const res = await createForum(fd);
      if (res.data.success === false) {
        toast.error(res.data.message || 'Failed to create forum');
        return;
      }
      clearForumSubscriptionCache();
      toast.success('Discussion forum created');
      navigate(`/forum/detail/${res.data.forum_id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create forum');
    } finally {
      setSubmitting(false);
    }
  };

  if (accessLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return null;
  }

  return (
    <div>
      <button
        type="button"
        className="back-button"
        onClick={() => navigate(forumListFallback)}
      >
        <FiArrowLeft style={{ marginRight: '0.5rem' }} />
        Back
      </button>

      <ForumForm
        onSubmit={handleSubmit}
        onCancel={() => navigate(forumListFallback)}
        users={users}
        branches={branches}
        departments={departments}
        subDepartments={subDepartments}
        submitting={submitting}
      />

      <InternalFooter />
    </div>
  );
};

export default ForumCreate;
